import { Grid } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { LastStepVM, SectionVM } from 'civil-status/view-models/step-form';
import ListAnsc from '../../../../../../shared/list-ansc';
import { useEventCallback } from '@mui/material';
import { ItemVM } from 'shell/view-models/item';
import { IdPropertyRegime } from 'civil-status/constants/id-property-regime';
import { FormControlLabel } from '@mui/material';
import { Checkbox } from '@mui/material';
import {
  TEXT_MARRIAGE_LAW,
  TEXT_UNION_CIVIL_LAW,
  TEXT_SEPARATION_ASSETS,
  TEXT_UNION_CIVIL_SEPARATION,
} from 'civil-status/constants/text-property-regime';
import { KeySections } from 'civil-status/constants/keys-subjects-section';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import FormNotarialDeed from './form-notarial-deed';

interface PropertyRegimeProps {
  formContext: FormikProps<LastStepVM>;
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  onChanges: (values: SectionVM) => void;
  controllPropertyRegime: (propertyRegime?: string, ageDefect?: string) => void;
  configuration?: string;
}

const PropertyRegimeComponent = ({
  formContext,
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  onChanges,
  controllPropertyRegime,
  configuration,
}: PropertyRegimeProps): React.ReactElement => {
  const valuesDatiSpecificiMatr: SectionVM = useMemo(() => {
    return (formContext.values[KeySections.keyDatiSpecificiMatr] || {}) as SectionVM;
  }, [formContext.values[KeySections.keyDatiSpecificiMatr]]);

  const isMarriage: boolean = useMemo(() => {
    return configuration === ConfigurationType.REGPAT01;
  }, [configuration]);

  useEffect(() => {
    controllPropertyRegime(values?.propertyRegime, valuesDatiSpecificiMatr.ageDefect);
  }, [values?.propertyRegime]);

  useEffect(() => {
    // Aggiornamento utile per i controllo dello schema nei DATI_SPECIFICI_MATR
    formContext.setFieldValue(KeySections.keyDatiSpecificiMatr, {
      ...valuesDatiSpecificiMatr,
      propertyRegime: values?.propertyRegime,
      minorGroomAssistance: '',
      minorWifeAssistance: '',
    });
  }, [values?.propertyRegime]);

  // id dell'annotazione se già inserita nel DB
  const idAnnotazione: number | undefined = useMemo(() => {
    return values?.annotationsPropertyRegime?.idAnnotazione;
  }, [values?.annotationsPropertyRegime]);

  const handlePropertyRegime = useEventCallback((item?: ItemVM) => {
    const isConvenzioniMatrimoniali =
      item?.id?.toString() === IdPropertyRegime.ConvenzioniMatrimoniali;
    const isAltraLeggeApplicabile = item?.id?.toString() === IdPropertyRegime.AltraLeggeApplicabile;
    const isSeparazioneDeiBeni = item?.id?.toString() === IdPropertyRegime.SeparazioneDeiBeni;

    onChanges({
      ...(values || {}),
      propertyRegime: item?.id?.toString() || '',
      lawPropertyRegime: isAltraLeggeApplicabile ? values?.lawPropertyRegime : '',
      annotationsPropertyRegime:
        isAltraLeggeApplicabile || isSeparazioneDeiBeni || isConvenzioniMatrimoniali
          ? {
              idAnnotazione,
              textContextualAnnotation: isAltraLeggeApplicabile
                ? isMarriage
                  ? `${TEXT_MARRIAGE_LAW.PrimaParte} ... ${TEXT_MARRIAGE_LAW.ChiusuraTesto}`
                  : `${TEXT_UNION_CIVIL_LAW.PrimaParte} ... ${TEXT_UNION_CIVIL_LAW.ChiusuraTesto}`
                : isSeparazioneDeiBeni
                ? isMarriage
                  ? TEXT_SEPARATION_ASSETS
                  : TEXT_UNION_CIVIL_SEPARATION
                : '',
              typeContextualAnnotation: item?.id?.toString(),
            }
          : {
              idAnnotazione,
            },
      isWithNotarialDeed: isConvenzioniMatrimoniali,
      // pulizia dei campi se non è ConvenzioniMatrimoniali
      nameNotary: isConvenzioniMatrimoniali ? values?.nameNotary || '' : '',
      surnameNotary: isConvenzioniMatrimoniali ? values?.surnameNotary || '' : '',
      notarialDistrict: isConvenzioniMatrimoniali ? values?.notarialDistrict || '' : '',
      datePropertyRegime: isConvenzioniMatrimoniali ? values?.datePropertyRegime || null : null,
      repertoireNumber: isConvenzioniMatrimoniali ? values?.repertoireNumber || '' : '',
      collectionNumber: isConvenzioniMatrimoniali ? values?.collectionNumber || '' : '',
    });
  });

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={6}>
        <ListAnsc
          label="Scelta del regime patrimoniale"
          idDominio="49"
          value={values?.propertyRegime || ''}
          onChange={handlePropertyRegime}
          disabled={disabled}
          error={Boolean(errors?.propertyRegime) && touched?.propertyRegime}
          required
        />
      </Grid>
      {values?.propertyRegime === IdPropertyRegime.AltraLeggeApplicabile ? (
        <Grid item xs={12} md={5}>
          <PmiTextField
            label="Legge regime patrimoniale *"
            aria-label="Legge regime patrimoniale"
            id="lawPropertyRegime"
            name="lawPropertyRegime"
            fullWidth
            helperText={errors?.lawPropertyRegime}
            error={touched?.lawPropertyRegime && Boolean(errors?.lawPropertyRegime)}
            type="text"
            variant="outlined"
            value={values?.lawPropertyRegime || ''}
            onChange={(event) => {
              let textContextualAnnotation = '';
              if (isMarriage) {
                textContextualAnnotation = event.target.value
                  ? `${TEXT_MARRIAGE_LAW.PrimaParte} ${event.target.value} ${TEXT_MARRIAGE_LAW.ChiusuraTesto}`
                  : `${TEXT_MARRIAGE_LAW.PrimaParte} ... ${TEXT_MARRIAGE_LAW.ChiusuraTesto}`;
              } else {
                textContextualAnnotation = event.target.value
                  ? `${TEXT_UNION_CIVIL_LAW.PrimaParte} ${event.target.value} ${TEXT_UNION_CIVIL_LAW.ChiusuraTesto}`
                  : `${TEXT_UNION_CIVIL_LAW.PrimaParte} ... ${TEXT_UNION_CIVIL_LAW.ChiusuraTesto}`;
              }

              onChanges({
                ...(values || {}),
                lawPropertyRegime: event.target.value,
                annotationsPropertyRegime: {
                  ...(values?.annotationsPropertyRegime || {}),
                  textContextualAnnotation,
                },
              });
            }}
          />
        </Grid>
      ) : undefined}
      {values?.propertyRegime === IdPropertyRegime.AltraLeggeApplicabile ||
      values?.propertyRegime === IdPropertyRegime.ConvenzioniMatrimoniali ? (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isNotCertifiable"
                checked={values?.annotationsPropertyRegime?.isNotCertifiable || false}
                onChange={() => {
                  onChange('annotationsPropertyRegime', {
                    ...(values?.annotationsPropertyRegime || {}),
                    isNotCertifiable: !values?.annotationsPropertyRegime?.isNotCertifiable,
                  });
                }}
                inputProps={{ 'aria-label': 'Dichiara come non certificabile' }}
              />
            }
            label="Dichiara come non certificabile"
          />
        </Grid>
      ) : undefined}
      {values?.isWithNotarialDeed &&
      values?.propertyRegime === IdPropertyRegime.ConvenzioniMatrimoniali ? (
        <FormNotarialDeed
          onChange={onChange}
          errors={errors}
          touched={touched}
          values={values}
          isMarriage={isMarriage}
        />
      ) : undefined}
      {values?.propertyRegime === IdPropertyRegime.SeparazioneDeiBeni ||
      values?.propertyRegime === IdPropertyRegime.ConvenzioniMatrimoniali ||
      values?.propertyRegime === IdPropertyRegime.AltraLeggeApplicabile ? (
        <Grid item xs={12}>
          <PmiTextField
            label="Testo annotazione contestuale"
            id="textContextualAnnotation"
            fullWidth
            multiline
            rows={5}
            value={values?.annotationsPropertyRegime?.textContextualAnnotation || ''}
            disabled
            onChange={(event) =>
              onChange('annotationsPropertyRegime', {
                ...(values?.annotationsPropertyRegime || {}),
                textContextualAnnotation: event.target.value,
              })
            }
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

PropertyRegimeComponent.displayName = 'Form Incorporation Certificate component';

const PropertyRegime = memo(PropertyRegimeComponent);

export default PropertyRegime;
